/**
 * Built-in levels and prefabs (Godot res:// tree).
 */

import type { SceneNodeData, SceneResource, ScriptRef } from './types';
import { createNode, defaultTransform, uid } from './types';
import {
  AI_SCRIPT_TEMPLATE,
  DEFAULT_SCRIPT_TEMPLATE,
  PLAYER_SCRIPT_TEMPLATE,
} from './ScriptRuntime';

function node(
  type: SceneNodeData['type'],
  name: string,
  opts: Partial<SceneNodeData> & { children?: SceneNodeData[] } = {},
): SceneNodeData {
  const n = createNode(type, name);
  Object.assign(n, opts);
  if (opts.transform) n.transform = { ...defaultTransform(), ...opts.transform, position: { ...defaultTransform().position, ...opts.transform?.position }, rotation: { ...defaultTransform().rotation, ...opts.transform?.rotation }, scale: { ...defaultTransform().scale, ...opts.transform?.scale } };
  if (opts.props) n.props = { ...n.props, ...opts.props };
  if (opts.children) n.children = opts.children;
  return n;
}

function script(path: string, source: string): ScriptRef {
  return { id: uid('script'), path, language: 'js', source };
}

function scene(
  path: string,
  name: string,
  kind: SceneResource['kind'],
  root: SceneNodeData,
  scripts: ScriptRef[],
  description: string,
): SceneResource {
  const scriptsMap: Record<string, ScriptRef> = {};
  for (const s of scripts) scriptsMap[s.id] = s;
  return {
    format: 1,
    id: `builtin_${path.replace(/[^\w]+/g, '_')}`,
    name,
    kind,
    path,
    root,
    scripts: scriptsMap,
    description,
    updatedAt: '2026-01-01T00:00:00.000Z',
    builtin: true,
  };
}

function makePlayerPrefab(): SceneResource {
  const playerScript = script('res://scripts/player_controller.js', PLAYER_SCRIPT_TEMPLATE);
  const root = node('CharacterBody3D', 'Player', {
    props: {
      characterId: 'fencer',
      colorId: 'blue',
      isPlayer: true,
      modelUrl: '/models/fencer-rigged.glb',
    },
    scriptId: playerScript.id,
    transform: { ...defaultTransform(), position: { x: -2, y: 0, z: 0 } },
    children: [
      node('Marker3D', 'TipAnchor', {
        transform: { ...defaultTransform(), position: { x: 0.4, y: 1.2, z: 0.6 } },
      }),
      node('Camera3D', 'FpsMount', {
        props: { fov: 70, near: 0.05, far: 100, current: false },
        transform: { ...defaultTransform(), position: { x: 0, y: 1.65, z: 0.1 } },
      }),
    ],
  });
  return scene(
    'res://prefabs/player.tscn',
    'Player',
    'prefab',
    root,
    [playerScript],
    'Player fencer prefab — CharacterBody3D + controller script + FPS mount',
  );
}

function makeOpponentPrefab(): SceneResource {
  const aiScript = script('res://scripts/ai_opponent.js', AI_SCRIPT_TEMPLATE);
  const root = node('CharacterBody3D', 'Opponent', {
    props: {
      characterId: 'fencer',
      colorId: 'red',
      isPlayer: false,
      modelUrl: '/models/fencer-rigged.glb',
    },
    scriptId: aiScript.id,
    scriptProps: { aggression: 0.6 },
    transform: {
      ...defaultTransform(),
      position: { x: 2, y: 0, z: 0 },
      rotation: { x: 0, y: 180, z: 0 },
    },
    children: [
      node('Marker3D', 'TipAnchor', {
        transform: { ...defaultTransform(), position: { x: 0.4, y: 1.2, z: 0.6 } },
      }),
    ],
  });
  return scene(
    'res://prefabs/opponent.tscn',
    'Opponent',
    'prefab',
    root,
    [aiScript],
    'AI opponent prefab with aggression export',
  );
}

function makePistePrefab(): SceneResource {
  const root = node('StaticBody3D', 'Piste', {
    children: [
      node('MeshInstance3D', 'Strip', {
        props: { mesh: 'piste', castShadow: true, receiveShadow: true },
      }),
      node('MeshInstance3D', 'Floor', {
        props: { mesh: 'plane', material: { preset: 'floor' }, castShadow: false, receiveShadow: true },
        transform: {
          ...defaultTransform(),
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: -90, y: 0, z: 0 },
          scale: { x: 20, y: 12, z: 1 },
        },
      }),
    ],
  });
  return scene('res://prefabs/piste.tscn', 'Piste', 'prefab', root, [], 'Competition strip + floor');
}

function makeScoringMachinePrefab(): SceneResource {
  const pulse = script(
    'res://scripts/scoring_pulse.js',
    `// @extends Node3D
// @export pulse_speed 2.5

function _ready() {
  this.print("Scoring machine online");
}

function _process(delta) {
  const t = performance.now() / 1000 * (this.pulse_speed || 2);
  // Subtle idle light bob on Y rotation
  this.set_rotation_degrees(0, Math.sin(t) * 3, 0);
}
`,
  );
  const root = node('Node3D', 'ScoringMachine', {
    scriptId: pulse.id,
    transform: { ...defaultTransform(), position: { x: 0, y: 0.12, z: -2.4 } },
    children: [
      node('MeshInstance3D', 'Pedestal', {
        props: { mesh: 'box', material: { preset: 'piste_wood' } },
        transform: {
          ...defaultTransform(),
          scale: { x: 0.8, y: 0.9, z: 0.5 },
          position: { x: 0, y: 0.45, z: 0 },
        },
      }),
      node('MeshInstance3D', 'Panel', {
        props: { mesh: 'box', material: { preset: 'guard' } },
        transform: {
          ...defaultTransform(),
          scale: { x: 0.7, y: 0.4, z: 0.1 },
          position: { x: 0, y: 1.1, z: 0.2 },
        },
      }),
      node('OmniLight3D', 'P1Light', {
        props: { color: '#3d9fff', energy: 2, range: 3 },
        transform: { ...defaultTransform(), position: { x: -0.2, y: 1.15, z: 0.3 } },
      }),
      node('OmniLight3D', 'P2Light', {
        props: { color: '#ff4d5e', energy: 2, range: 3 },
        transform: { ...defaultTransform(), position: { x: 0.2, y: 1.15, z: 0.3 } },
      }),
    ],
  });
  return scene(
    'res://prefabs/scoring_machine.tscn',
    'ScoringMachine',
    'prefab',
    root,
    [pulse],
    'FIE-style scoring pedestal with pulse script',
  );
}

function makeMainArena(): SceneResource {
  const spinDemo = script('res://scripts/banner_spin.js', DEFAULT_SCRIPT_TEMPLATE);
  const root = node('Node3D', 'MainArena', {
    children: [
      node('DirectionalLight3D', 'KeyLight', {
        props: { color: '#fff2e0', energy: 2.0 },
        transform: {
          ...defaultTransform(),
          position: { x: 4, y: 10, z: 6 },
          rotation: { x: -50, y: 30, z: 0 },
        },
      }),
      node('DirectionalLight3D', 'FillLight', {
        props: { color: '#6688ff', energy: 0.55 },
        transform: {
          ...defaultTransform(),
          position: { x: -6, y: 4, z: -4 },
          rotation: { x: -30, y: -40, z: 0 },
        },
      }),
      node('OmniLight3D', 'BlueFoot', {
        props: { color: '#3d7bff', energy: 6, range: 10 },
        transform: { ...defaultTransform(), position: { x: -5, y: 0.4, z: 1.2 } },
      }),
      node('OmniLight3D', 'RedFoot', {
        props: { color: '#ff4466', energy: 6, range: 10 },
        transform: { ...defaultTransform(), position: { x: 5, y: 0.4, z: 1.2 } },
      }),
      node('PackedScene', 'PisteInstance', {
        props: { scenePath: 'res://prefabs/piste.tscn' },
      }),
      node('PackedScene', 'PlayerInstance', {
        props: { scenePath: 'res://prefabs/player.tscn' },
        transform: { ...defaultTransform(), position: { x: -2, y: 0, z: 0 } },
      }),
      node('PackedScene', 'OpponentInstance', {
        props: { scenePath: 'res://prefabs/opponent.tscn' },
        transform: {
          ...defaultTransform(),
          position: { x: 2, y: 0, z: 0 },
          rotation: { x: 0, y: 180, z: 0 },
        },
      }),
      node('PackedScene', 'MachineInstance', {
        props: { scenePath: 'res://prefabs/scoring_machine.tscn' },
      }),
      node('Camera3D', 'SideCamera', {
        props: { fov: 40, near: 0.1, far: 80, current: true },
        transform: {
          ...defaultTransform(),
          position: { x: 0, y: 2.2, z: 8 },
          rotation: { x: -8, y: 0, z: 0 },
        },
      }),
      node('Camera3D', 'ThirdPersonCam', {
        props: { fov: 50, near: 0.1, far: 80, current: false },
        transform: {
          ...defaultTransform(),
          position: { x: -4, y: 1.8, z: 3 },
          rotation: { x: -10, y: 40, z: 0 },
        },
      }),
      node('MeshInstance3D', 'SponsorBanner', {
        props: { mesh: 'box', material: { preset: 'jacket' }, castShadow: true },
        scriptId: spinDemo.id,
        scriptProps: { speed: 0.35, amplitude: 0.05 },
        transform: {
          ...defaultTransform(),
          position: { x: 0, y: 3.5, z: -7.5 },
          scale: { x: 6, y: 1.2, z: 0.08 },
        },
      }),
      node('Marker3D', 'EnGardeP1', {
        transform: { ...defaultTransform(), position: { x: -2, y: 0, z: 0 } },
      }),
      node('Marker3D', 'EnGardeP2', {
        transform: { ...defaultTransform(), position: { x: 2, y: 0, z: 0 } },
      }),
      node('Timer', 'BoutPulseTimer', {
        props: { waitTime: 5, oneShot: false, autostart: true },
      }),
      node('Area3D', 'CenterZone', {
        props: { monitoring: true, collisionLayer: 1, collisionMask: 1 },
        transform: { ...defaultTransform(), position: { x: 0, y: 0.9, z: 0 }, scale: { x: 1.5, y: 1.5, z: 1.2 } },
        children: [
          node('CollisionShape3D', 'Shape', {
            props: { shape: 'box', shapeSize: { x: 2, y: 2, z: 1.5 } },
          }),
        ],
      }),
      node('AudioStreamPlayer3D', 'Announcer', {
        props: { stream: 'allez', autoplayAudio: false, volumeDb: -3 },
        transform: { ...defaultTransform(), position: { x: 0, y: 2.5, z: -2 } },
      }),
    ],
  });
  return scene(
    'res://levels/main_arena.tscn',
    'MainArena',
    'level',
    root,
    [spinDemo],
    'Primary bout venue — lights, piste, player/opponent prefabs, cameras, scoring machine',
  );
}

function makeTutorialHall(): SceneResource {
  const coach = script(
    'res://scripts/tutorial_coach.js',
    `// @extends Node3D
// @export step 0

function _ready() {
  this.print("Maître ready — Academy tutorial hall");
  this._t = 0;
}

function _process(delta) {
  this._t = (this._t || 0) + delta;
  // Gentle coach nod
  this.set_rotation_degrees(Math.sin(this._t) * 3, 20, 0);
}
`,
  );
  const root = node('Node3D', 'TutorialHall', {
    children: [
      node('DirectionalLight3D', 'Sun', {
        props: { color: '#ffe8c8', energy: 1.4 },
        transform: {
          ...defaultTransform(),
          position: { x: 2, y: 8, z: 3 },
          rotation: { x: -45, y: 15, z: 0 },
        },
      }),
      node('MeshInstance3D', 'Floor', {
        props: { mesh: 'plane', material: { preset: 'floor' }, receiveShadow: true },
        transform: {
          ...defaultTransform(),
          rotation: { x: -90, y: 0, z: 0 },
          scale: { x: 12, y: 8, z: 1 },
        },
      }),
      node('MeshInstance3D', 'ShortPiste', {
        props: { mesh: 'piste' },
      }),
      node('CharacterBody3D', 'Student', {
        props: {
          characterId: 'fencer',
          colorId: 'blue',
          isPlayer: true,
          modelUrl: '/models/fencer-rigged.glb',
        },
        transform: { ...defaultTransform(), position: { x: -1.5, y: 0, z: 0 } },
      }),
      node('Node3D', 'Maitre', {
        scriptId: coach.id,
        transform: { ...defaultTransform(), position: { x: 0, y: 0, z: -1.5 } },
        children: [
          node('MeshInstance3D', 'Body', {
            props: { mesh: 'capsule', material: { preset: 'jacket' } },
            transform: {
              ...defaultTransform(),
              position: { x: 0, y: 0.95, z: 0 },
            },
          }),
        ],
      }),
      node('Camera3D', 'TutorialCam', {
        props: { fov: 45, current: true },
        transform: {
          ...defaultTransform(),
          position: { x: 0, y: 2, z: 6 },
          rotation: { x: -10, y: 0, z: 0 },
        },
      }),
    ],
  });
  return scene(
    'res://levels/tutorial_hall.tscn',
    'TutorialHall',
    'level',
    root,
    [coach],
    'Academy tutorial space with Maître coach script',
  );
}

function makeEmptyLevel(): SceneResource {
  const root = node('Node3D', 'NewLevel', {
    children: [
      node('DirectionalLight3D', 'Light', {
        props: { color: '#ffffff', energy: 1.2 },
        transform: {
          ...defaultTransform(),
          position: { x: 3, y: 6, z: 4 },
          rotation: { x: -40, y: 25, z: 0 },
        },
      }),
      node('MeshInstance3D', 'Ground', {
        props: { mesh: 'plane', material: { preset: 'floor' }, receiveShadow: true },
        transform: {
          ...defaultTransform(),
          rotation: { x: -90, y: 0, z: 0 },
          scale: { x: 10, y: 10, z: 1 },
        },
      }),
      node('Camera3D', 'Camera', {
        props: { fov: 50, current: true },
        transform: {
          ...defaultTransform(),
          position: { x: 4, y: 3, z: 6 },
          rotation: { x: -20, y: 30, z: 0 },
        },
      }),
    ],
  });
  return scene('res://levels/empty.tscn', 'EmptyLevel', 'level', root, [], 'Blank level template');
}

let _cache: SceneResource[] | null = null;

export function getBuiltinScenes(): SceneResource[] {
  if (_cache) return _cache.map((s) => structuredClone(s));
  _cache = [
    makeMainArena(),
    makeTutorialHall(),
    makeEmptyLevel(),
    makePlayerPrefab(),
    makeOpponentPrefab(),
    makePistePrefab(),
    makeScoringMachinePrefab(),
  ];
  return _cache.map((s) => structuredClone(s));
}

export function getBuiltinSceneByPath(path: string): SceneResource | null {
  const all = getBuiltinScenes();
  return all.find((s) => s.path === path) ?? null;
}
